/** Soft-warn thresholds for km entry / correction */
export const KM_WARN_DISTANCE = 350
export const KM_EXTRA_DIGIT_MIN_DISTANCE = 500

export function parseKmDigits(raw) {
  const km = parseInt(String(raw == null ? '' : raw).replace(/\D/g, ''), 10)
  return Number.isNaN(km) ? 0 : km
}

export function bookingDurationDays(start, end) {
  if (!start || !end) return 1
  const startMs = Date.parse(String(start).replace(' ', 'T'))
  const endMs = Date.parse(String(end).replace(' ', 'T'))
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs <= startMs) {
    return 1
  }
  const hours = (endMs - startMs) / (1000 * 60 * 60)
  return Math.max(1, Math.ceil(hours / 24))
}

export function unlikelyDistanceThreshold(start, end) {
  const days = bookingDurationDays(start, end)
  return Math.max(KM_WARN_DISTANCE, days * 120)
}

/**
 * @returns {{ ok: boolean, error?: string, warnings: string[], distance: number }}
 */
export function assessKmStands({
  startKm,
  endKm,
  bookingStart,
  bookingEnd,
  endPrefillPrefix,
  neighbors
}) {
  const warnings = []
  if (startKm <= 0 && endKm <= 0) {
    return { ok: false, error: 'Angiv mindst én km-stand.', warnings, distance: 0 }
  }
  if (endKm > 0 && endPrefillPrefix && String(endKm) === String(endPrefillPrefix)) {
    return {
      ok: false,
      error: 'Tilføj de sidste cifre i slut-km, før du gemmer.',
      warnings,
      distance: 0
    }
  }
  if (startKm > 0 && endKm > 0 && endKm < startKm) {
    return {
      ok: false,
      error: 'Slut-km må ikke være lavere end start-km. Ret venligst km-standene.',
      warnings,
      distance: 0
    }
  }

  const distance = startKm > 0 && endKm > 0 ? endKm - startKm : 0
  if (distance > 0) {
    const startDigits = String(startKm).length
    const endDigits = String(endKm).length
    if (endDigits === startDigits + 1 && distance >= KM_EXTRA_DIGIT_MIN_DISTANCE) {
      warnings.push(
        `Slut-km har ét ciffer mere end start-km, og distancen er ${distance} km. Er der tastet et ciffer for meget?`
      )
    }
    const threshold = unlikelyDistanceThreshold(bookingStart, bookingEnd)
    if (distance > threshold) {
      warnings.push(
        `Distancen ${distance} km er usædvanlig høj for denne booking (typisk under ${threshold} km).`
      )
    }
  }

  // Soft note when correction may leave a gap vs adjacent bookings (no cascade)
  const prev = neighbors && neighbors.previous
  const next = neighbors && neighbors.next
  if (prev && startKm > 0 && Number(prev.kmStandEnd) > 0 && startKm !== Number(prev.kmStandEnd)) {
    warnings.push(
      'Start-km matcher ikke forrige bookings slut-km. En administrator kan rette nabobookingen under Forbrug, hvis det er nødvendigt.'
    )
  }
  if (next && endKm > 0 && Number(next.kmStandStart) > 0 && endKm !== Number(next.kmStandStart)) {
    warnings.push(
      'Slut-km matcher ikke næste bookings start-km. En administrator kan rette nabobookingen under Forbrug, hvis det er nødvendigt.'
    )
  }

  return { ok: true, warnings, distance }
}

/** Extract Danish friendly message from API/axios errors. */
export function friendlyKmApiError(error, fallback = 'Kunne ikke gemme km-stande.') {
  const data = error && error.response && error.response.data
  if (data && data.error && data.error.friendly && data.error.friendly.da) {
    return data.error.friendly.da
  }
  if (data && data.friendly && data.friendly.da) {
    return data.friendly.da
  }
  if (error && error.responseData && error.responseData.error && error.responseData.error.friendly) {
    return error.responseData.error.friendly.da || fallback
  }
  if (error && error.message && !/^Request failed with status code \d+/.test(error.message)) {
    return error.message
  }
  return fallback
}

export function buildKmConfirmMessage({ startKm, endKm, distance, warnings }) {
  const lines = []
  if (warnings && warnings.length) {
    lines.push(warnings.join('\n\n'))
    lines.push('')
  }
  if (startKm > 0 && endKm > 0) {
    lines.push(`Du har kørt ${distance} km`)
    lines.push(`(${startKm} → ${endKm})`)
  } else if (startKm > 0) {
    lines.push(`Gem start-km: ${startKm}`)
  } else {
    lines.push(`Gem slut-km: ${endKm}`)
  }
  lines.push('')
  lines.push('Er det korrekt?')
  return lines.join('\n')
}
